
import Vue from 'vue';
import { mixins } from 'vue-class-component';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import { RootState } from '@/store/store';
import { ResearchNode } from '@/store/modules/documents/types/documents.types';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import ActiveMultiSelect from '@/components/form/ActiveMultiSelect.vue';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import {
  UpdatePendingProjectMixin,
  EditPendingProjectFormValues
} from '../mixins/update-pending-project.mixin';
import { PendingProjectSettings } from '@/store/modules/pending-documents/pending-documents.state';

const initialFormValue: EditPendingProjectFormValues = {
  projectTitle: '',
  primaryResearchNode: undefined,
  researchNodes: []
};

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    ActiveMultiSelect
  }
})
export default class PendingProjectSettingsForm extends mixins(
  UpdatePendingProjectMixin
) {
  @Prop({ type: Number, required: true })
  public documentId!: number;

  @Prop({ default: '250px' })
  public containerMinHeight!: string;

  @State((state: RootState) => state.projects.activeResearchNodes)
  public researchNodes!: ResearchNode[];

  @State(
    (state: RootState) =>
      state.pendingDocuments.apiState.updateProjectSettings.loading
  )
  public updating!: boolean;

  @Action('pendingDocuments/getProjectSettings')
  public getProjectSettings!: (payload: {
    documentId: number;
  }) => Promise<PendingProjectSettings>;

  @Action('projects/getActiveResearchNodes')
  public getActiveResearchNodes!: () => void;

  @Action('pendingDocuments/clearProjectSettings')
  public clearProjectSettings!: (payload: { documentId: number }) => void;

  public form: EditPendingProjectFormValues = { ...initialFormValue };
  public projectSettingsData: PendingProjectSettings | null = null;
  public isLoadingData: boolean = false;

  get validatorInvalid(): boolean {
    return (this.$refs.observer as any)?.flags?.invalid || false;
  }

  get secondaryResearchNodesOptions(): ResearchNode[] {
    if (!this.form.primaryResearchNode) {
      return [];
    }
    return this.researchNodes.filter(
      (node: ResearchNode) => node.id !== this.form.primaryResearchNode!.id
    );
  }

  get projectSettings(): PendingProjectSettings | null {
    const storeSettings =
      (this.$store.state as RootState).pendingDocuments.projectSettings[
        this.documentId
      ] || null;

    // Prefer local data over store data during loading to avoid race conditions
    return this.projectSettingsData || storeSettings;
  }

  get isFormReady(): boolean {
    return (
      !this.isLoadingData &&
      !!this.projectSettings &&
      !!this.researchNodes &&
      this.researchNodes.length > 0
    );
  }

  // Removed watchers - form initialization is now handled directly in refreshFormData

  private initializeFormIfReady() {
    if (
      !this.projectSettings ||
      !this.researchNodes ||
      this.researchNodes.length === 0
    ) {
      // Reset form to initial values when data is not ready
      this.form = { ...initialFormValue };
      return;
    }

    // Initialize form with store data - preserve order from project settings
    this.form = {
      projectTitle: this.projectSettings.projectTitle || '',
      primaryResearchNode: this.researchNodes.find(
        (node) => node.id === this.projectSettings!.primaryResearchNode?.id
      ),
      // Preserve the order from stored project settings rather than from available research nodes
      researchNodes: (this.projectSettings.researchNodes || [])
        .map((savedNode) =>
          this.researchNodes.find(
            (availableNode) => availableNode.id === savedNode.id
          )
        )
        .filter(Boolean) as ResearchNode[]
    };
  }

  public async created() {
    // Data loading is now handled in mounted() to ensure fresh data on each modal open
  }

  public async handleValidateAndSubmit(): Promise<void> {
    const validated = await (this.$refs.observer as any).validate();
    if (validated === false) return;
    this.handleSubmit();
  }

  public handleSubmit() {
    this.editPendingProject({
      ...this.form,
      pendingDocumentId: this.documentId
    });
  }

  @Watch('form.primaryResearchNode')
  public onPrimaryResearchNodeChanged(
    newVal: ResearchNode | undefined,
    oldVal: ResearchNode | undefined
  ) {
    if (!newVal) return;
    // Remove the primary node from secondary nodes if present
    this.form.researchNodes = this.form.researchNodes.filter(
      (node) => node.id !== newVal.id
    );
  }

  @Watch('updateProjectSettingsSuccess')
  @isDifferent
  @isTruthy
  public onUpdateSuccess() {
    // Clear local project settings data to ensure fresh data from store is used
    this.projectSettingsData = null;
    this.$emit('close');
  }

  public async mounted() {
    // Always refresh data when modal is opened/mounted
    await this.refreshFormData();
  }

  private async refreshFormData() {
    this.isLoadingData = true;

    try {
      // Always load research nodes first to ensure they're available
      await this.getActiveResearchNodes();

      // Clear cached project settings to ensure fresh data
      this.clearProjectSettings({ documentId: this.documentId });
      this.projectSettingsData = null;

      // Always fetch fresh project settings from the server
      const settings = await this.getProjectSettings({
        documentId: this.documentId
      });

      // Store in local state as fallback - using $set to ensure reactivity
      this.$set(this, 'projectSettingsData', settings);

      // Wait for next tick to ensure all reactive updates are processed
      await this.$nextTick();

      // Initialize form with loaded data
      this.initializeFormIfReady();
    } catch (error) {
      console.error('Error in refreshFormData:', error);
      this.$buefy.toast.open({
        message: 'Failed to load project settings. Please try again.',
        type: 'is-danger',
        position: 'is-top'
      });
      this.$emit('close');
    } finally {
      this.isLoadingData = false;
    }
  }
}
